<template>
	<div class="numpad-container">
		<div class="numpad-container-background">
			<div class="numpad-screen-container">
				<div class="numpad-screen">
					{{ currencyStore.readableCurrencyValue(numpadNumber) }}
				</div>
			</div>
			<div class="numpad-actions-container">
				<div class="numpad">
					<BottomNumberEditorNumpad no-actions ref="numpad" @change-number="v => numpadNumber = v" :startingNumber="0" />
				</div>
				<div class="numpad-actions">
					<div class="numpad-action-container action-open-summary" :class="{disabled: counterStore.cartItemsCount === 0}" @click="ordersStore.showMobileOrderSummary ? ordersStore.deactivateMobileOrderSummary() : ordersStore.activateMobileOrderSummary()"><div class="numpad-action" v-html="svgStore.get('iconOperations')"></div></div>
					<div class="numpad-action-container action-pay-shortcut" :class="{disabled: payDisabled}" @click="clickPay"><div class="numpad-action" v-html="svgStore.get('iconPos')"></div></div>
					<div class="numpad-action-container action-add" :class="{disabled: numpadNumber === 0}" @click="clickAdd"><div class="numpad-action" v-html="svgStore.get('iconPlus')"></div></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BottomNumberEditorNumpad from "@/console-new/components/ops/counter/BottomNumberEditorNumpad.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useAnalyticsStore, useOrdersStore, useUserStore} from "@/console-new/stores";
import {useCounterStore} from "@/console-new/stores/counter.store";
import Hash from "@/shared/helpers/Hash";
import API from "@/shared/helpers/API";
import {getLocalDateFromServer} from "@/shared/helpers/dates";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let svgStore = useSvgStore();
		let ordersStore = useOrdersStore();
		let counterStore = useCounterStore();
		let analyticsStore = useAnalyticsStore();
		const currencyStore = useCurrencyStore();
		return {languageStore, getString: languageStore.getString, userStore, currencyStore, svgStore, ordersStore, counterStore, analyticsStore, showNotification: useNotificationStore().showNotification};
	},
	name: "CounterNumpad",
	components: {BottomNumberEditorNumpad},
	data() {
		return {
			numpadNumber: 0,
		}
	},
	computed: {
		payDisabled() {
			if(!this.userStore.checkAnyOrderPayAllowed) {
				return true;
			}
			return this.numpadNumber === 0;
		}
	},
	methods: {
		clickPay() {
			if(!this.userStore.checkOrderCreationAllowed){
				this.showNotification('permission-denied-order-creation');
				return;
			}
			if(this.numpadNumber === 0) {
				return;
			}
			// Fast-pay only the typed amount as a standalone payment, like the cards shortcut.
			this.counterStore.startQuickPay(this.numpadNumber);
			this.$refs["numpad"].cancel();
		},
		clickAdd() {
			if(!this.userStore.checkOrderCreationAllowed){
				this.showNotification('permission-denied-order-creation');
				return;
			}

			if (this.numpadNumber > 0 && this.userStore.currBusiness.counter_manual_code) {
				const salt = "s#4lT!";
				const numpadNumber = this.numpadNumber.toString();
				const hash = Hash.init().calculate(salt + numpadNumber);
				const cutoffHash = Hash.init().calculate(salt + numpadNumber.slice(0, numpadNumber.length-2));

				if (hash === this.userStore.currBusiness.counter_manual_code){
					this.switchToManualConsole();
				}
				else if(cutoffHash === this.userStore.currBusiness.counter_manual_code && numpadNumber.slice(numpadNumber.length - 2) === "00") {
					API.init().updateBusinessCutoff(this.userStore.currBusinessId, numpadNumber, res => {
						if (res && res.result === 'OK') {
							this.userStore.currBusiness.counter_manual_cutoff_date = getLocalDateFromServer(res.new_cutoff_date, false);
							this.userStore.currBusiness.counter_manual_cutoff_date.setHours(0,0,0,0);
							this.switchToManualConsole();
						}else {
							this.addFakeItem();
						}
					});
				}
				else {
					this.addFakeItem();
				}
			}
			else {
				this.addFakeItem();
			}
		},
		addFakeItem() {
			this.counterStore.addCartFakeItem(this.numpadNumber);
			this.$refs["numpad"].cancel();
		},
		switchToManualConsole() {
			let metaThemeColor = document.querySelector('meta[name="theme-color"]');
			if(this.userStore.manualCode === null) {

				let lastTwoChar = this.numpadNumber.toString().match(/(.{2})$/);
				if (lastTwoChar && lastTwoChar[0] === "00"){
					this.userStore.manualCode = this.numpadNumber.toString().slice(0, -2);
				}
				else {
					this.userStore.manualCode = this.numpadNumber;
				}

				if(metaThemeColor && metaThemeColor?.setAttribute) {
					metaThemeColor.setAttribute('content', '#000');
				}
			} else {
				this.userStore.manualCode = null;
				if(metaThemeColor && metaThemeColor?.setAttribute) {
					metaThemeColor.setAttribute('content', '#0752A3');
				}
			}
			this.analyticsStore.loadedOrders = false;
			this.analyticsStore.loadedProducts = false;
			this.analyticsStore.loadedTables = false;
			this.analyticsStore.loadedCustomers = false;
			this.$refs["numpad"].cancel();
		}
	}
}
</script>

<style scoped lang="scss">
.numpad-container {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: var(--orders-padding);

	.numpad-container-background {
		background-color: var(--color-bg-primary-1);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		padding: 30px;
		border-radius: 26px;
		box-shadow: inset 1px 1px 4px var(--color-alpha-2), 4px 4px 25px -10px var(--color-alpha-8);

		@media (max-width: 800px) and (min-width: 750px) {
			padding: 20px;
		}

		@media (max-width: 450px) {
			padding: 12px;
		}

		@media (max-width: 400px) {
			padding: 20px;
		}

		@media (max-width: 320px) {
			box-shadow: none;
			background-color: transparent;
			padding: 0;
		}

		.numpad-screen-container {
			width: 350px;
			margin-bottom: 10px;

			.numpad-screen {
				background-color: var(--color-alpha-4);
				border-radius: 13px;
				box-shadow: inset 0 1px 2px var(--color-alpha-4);
				display: flex;
				align-items: center;
				justify-content: flex-end;
				min-height: 120px;
				font-size: 38px;
				font-weight: 500;
				padding: 4px 12px;
				color: var(--color-txt-white);
				overflow: hidden;
			}

			@media (max-width: 400px) {
				width: 255px;
				margin-bottom: 8px;

				.numpad-screen {
					font-size: 32px;
					min-height: 80px;
				}
			}

			@media (max-height: 680px) {
				.numpad-screen {
					min-height: 50px;
					font-size: 32px;
				}
			}

			@media (max-height: 560px) {
				width: 215px;
				margin-bottom: 8px;
				.numpad-screen {
					min-height: 40px;
					font-size: 24px;
				}
			}

			@media (max-height: 440px) or (max-width: 300px) {
				width: 172px;
				margin-bottom: 6px;
			}

			@media (max-height: 380px) {
				width: 148px;
				.numpad-screen {
					font-size: 22px;
				}
			}

		}

		@media (max-height: 600px) {
			padding: 12px;
		}

		.numpad-actions-container {
			display: flex;
			align-items: stretch;
			justify-content: center;

			.numpad {

				&:deep(.numpad) {

					@media (max-width: 400px) {
						gap: 5px;

						.row {
							gap: 5px;

							.btn {
								width: 60px;
								height: 60px;
								border-radius: 18px;

								.btn-inner {
									border-radius: 15px;
									width: 77%;
									height: 77%;
									font-size: 36px;
								}

								&.action .btn-inner {
									font-size: 32px;
								}
							}
						}
					}
					@media (max-height: 560px) {
						gap: 5px;

						.row {
							gap: 5px;

							.btn {
								width: 50px;
								height: 50px;
								border-radius: 12px;

								.btn-inner {
									border-radius: 10px;
									width: 77%;
									height: 77%;
									font-size: 30px;
								}

								&.action .btn-inner {
									font-size: 26px;
								}
							}
						}
					}
					@media (max-height: 440px) or (max-width: 300px) {
						gap: 4px;

						.row {
							gap: 4px;

							.btn {
								width: 40px;
								height: 40px;
								border-radius: 10px;

								.btn-inner {
									border-radius: 8px;
									width: 77%;
									height: 77%;
									font-size: 24px;
								}

								&.action .btn-inner {
									font-size: 21px;
								}
							}
						}
					}
					@media (max-height: 380px) {
						.row {
							.btn {
								width: 34px;
								height: 34px;
								border-radius: 8px;

								.btn-inner {
									border-radius: 8px;
									width: 77%;
									height: 77%;
									font-size: 18px;
								}

								&.action .btn-inner {
									font-size: 18px;
								}
							}
						}
					}
				}

			}
			.numpad-actions {
				margin-left: 10px;
				display: flex;
				flex-direction: column;
				align-items: center;
				width: 80px;
				gap: 10px;

				@media (max-width: 400px) {
					gap: 5px;
					margin-left: 5px;
					width: 60px;
				}
				@media (max-height: 560px) {
					gap: 5px;
					margin-left: 5px;
					width: 50px;
				}
				@media (max-height: 440px) or (max-width: 300px) {
					gap: 4px;
					margin-left: 4px;
					width: 40px;
				}
				@media (max-height: 380px) {
					width: 34px;
				}

				.numpad-action-container {
					border-radius: 26px;
					width: 80px;
					display: flex;
					justify-content: center;
					align-items: center;
					transition: opacity 0.2s ease-in-out;
					background-color: rgb(185, 204, 236);
					color: $primary;

					.numpad-action  {
						border-radius: 20px;
						display: flex;
						justify-content: center;
						align-items: center;
						width: 70%;
						height: 92%;
						transition: box-shadow 0.1s ease-in-out;
						@include noSelect();
						background-color: rgb(185, 204, 236);
						font-size: 36px;
						box-shadow: -3px -3px 6px rgba(255, 255, 255, 0.54), inset 3px 3px 6px rgba(0, 0, 0, 0), 3px 3px 6px rgba(0, 0, 0, 0.18), inset -3px -3px 6px rgba(255, 255, 255, 0);

						&:deep(svg) {
							width: 55%;
							height: 55%;

							.fill {
								fill: var(--color-svg-primary);
							}

							.stroke {
								stroke: var(--color-svg-primary);
							}
						}
					}

					cursor: pointer;
					box-shadow: -3px -3px 6px rgba(255, 255, 255, 0), inset 3px 3px 6px rgba(0, 0, 0, 0.18), 3px 3px 6px rgba(0, 0, 0, 0), inset -3px -3px 6px rgba(255, 255, 255, 0.54);

					&:active .numpad-action {
						box-shadow: -3px -3px 6px rgba(255, 255, 255, 0), inset 3px 3px 6px rgba(0, 0, 0, 0.18), 3px 3px 6px rgba(0, 0, 0, 0), inset -3px -3px 6px rgba(255, 255, 255, 0.54);
					}

					&.action-open-summary,
					&.action-pay-shortcut {
						flex: 0 0 auto;
						width: 80px;
						height: 80px;
						border-radius: 26px;

						.numpad-action  {
							width: 70%;
							height: 70%;
							border-radius: 20px;

							&:deep(svg) {
								width: 55%;
								height: 55%;
							}
						}

						@media (max-width: 400px) {
							width: 60px;
							height: 60px;
							border-radius: 18px;

							.numpad-action  {
								width: 77%;
								height: 77%;
								border-radius: 15px;
							}
						}

						@media (max-height: 560px) {
							width: 50px;
							height: 50px;
							border-radius: 12px;

							.numpad-action  {
								width: 77%;
								height: 77%;
								border-radius: 10px;
							}
						}

						@media (max-height: 440px) or (max-width: 300px) {
							width: 40px;
							height: 40px;
							border-radius: 10px;

							.numpad-action  {
								width: 77%;
								height: 77%;
								border-radius: 8px;
							}
						}

						@media (max-height: 380px) {
							width: 34px;
							height: 34px;
							border-radius: 8px;

							.numpad-action  {
								width: 77%;
								height: 77%;
								border-radius: 8px;
							}
						}
					}


					&.action-open-summary {
						display: none;

						@media (max-width: 750px) {
							display: flex;
						}
					}

					&.action-add {
						flex: 1 1 auto;
						padding: 12px 0;

						.numpad-action {
							width: 70%;
							height: 100%;
							border-radius: 20px;
						}

						@media (max-width: 400px) {
							width: 60px;
							border-radius: 18px;
							padding: 7px 0;

							.numpad-action  {
								width: 77%;
								font-size: 36px;
								border-radius: 15px;

								&:deep(svg) {
									width: 55%;
									height: 55%;
								}
							}
						}

						@media (max-height: 560px) {
							width: 50px;
							border-radius: 12px;
							padding: 6px 0;
							.numpad-action {
								width: 77%;
								border-radius: 10px;
							}
						}

						@media (max-height: 440px) or (max-width: 300px) {
							width: 40px;
							border-radius: 10px;
							padding: 5px 0;
							.numpad-action {
								width: 77%;
								border-radius: 8px;
							}
						}

						@media (max-height: 380px) {
							width: 34px;
							border-radius: 8px;
							padding: 4px 0;
							.numpad-action {
								width: 77%;
								border-radius: 8px;
							}
						}
					}

					&.disabled {
						cursor: default;
						pointer-events: none;
						opacity: 0.5;
					}

				}
			}
		}
	}

}
</style>